import {html} from "lit";
import {NOTIFICATION_DURATION} from "../constants";
import type {QuickLinkEnvironment} from "./interfaces/quick-link-environment.interface";
import type {QuickLinkCopyResult} from "../types/quick-link-copy-result.type";
import {buildKnowledgeObjectPath} from "./knowledge-object-path.helper";
import {UiHelper} from "./ui.helper";

/** How long the toast with the link to select by hand stays: long enough to mark and copy it. */
const MANUAL_COPY_TOAST_DURATION = 30_000;

/**
 * Reads what the page offers for a quick link. The knowledge quick link reads `isSecureContext`,
 * `location` and `navigator` only here: the components call this instead, so their render code
 * also runs where those globals do not exist (the frontend's unit tests run in a node environment).
 */
export function quickLinkEnvironment(): QuickLinkEnvironment {
    return {
        isSecure: globalThis.isSecureContext === true,
        origin: globalThis.location?.origin ?? '',
        clipboard: globalThis.navigator?.clipboard,
    };
}

/** The absolute quick link of a whiteboard or document: the page origin plus its route path. */
export function buildKnowledgeQuickLink(origin: string, uid: string, title: string): string {
    return origin + buildKnowledgeObjectPath(uid, title);
}

/**
 * Writes the link to the clipboard. Without a clipboard (plain http) the user has to copy it by
 * hand; a refused write carries the reason, with the issue dialog's fallback text when the
 * rejection has no message.
 */
export async function copyKnowledgeQuickLink(url: string, environment: QuickLinkEnvironment): Promise<QuickLinkCopyResult> {
    if (!environment.clipboard) {
        return 'manual';
    }
    try {
        await environment.clipboard.writeText(url);

        return 'copied';
    } catch (error: unknown) {
        return {failed: (error as { message?: string } | null)?.message ?? 'unbekannter Fehler'};
    }
}

/**
 * Copies the link and tells the user how it went, with the texts of the issue dialog's quick link
 * (`ha-dialog-issue-edit-orchestrator.component.ts`). Without a clipboard the toast shows the link
 * to select by hand.
 */
export async function copyKnowledgeQuickLinkAndNotify(url: string, environment: QuickLinkEnvironment): Promise<void> {
    const result = await copyKnowledgeQuickLink(url, environment);

    if (result === 'copied') {
        UiHelper.notify({
            closable: true,
            title: 'URL kopiert',
            level: 'info',
            content: html`<p>Quicklink URL wurde in die Zwischenablage kopiert.</p>`,
            duration: NOTIFICATION_DURATION,
        });
        return;
    }

    if (result === 'manual') {
        UiHelper.notify({
            closable: true,
            title: 'Quicklink',
            level: 'info',
            content: html`<span class="quicklink-text">${url}</span>`,
            duration: MANUAL_COPY_TOAST_DURATION,
        });
        return;
    }

    UiHelper.notify({
        closable: true,
        title: 'Kopieren nicht möglich.',
        level: 'error',
        content: html`<p>Konnte URL nicht in Zwischenablage einfügen:<br>${result.failed}</p>`,
    });
}
